import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const HomeAffordability = () => {
  // State variables for Home Affordability calculation
  const [annualIncome, setAnnualIncome] = useState(0); // Annual income of the individual
  const [affordabilityRatio, setAffordabilityRatio] = useState(0); // Affordability ratio
  const [debt, setDebt] = useState(0); // Existing debt
  const [maxHomePrice, setMaxHomePrice] = useState(0); // Calculated maximum home price

  // Event handlers to update state values
  const handleAnnualIncomeChange = (e) =>
    setAnnualIncome(parseFloat(e.target.value) || 0);
  const handleAffordabilityRatioChange = (e) =>
    setAffordabilityRatio(parseFloat(e.target.value) || 0);
  const handleDebtChange = (e) => setDebt(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate max home price when any input changes
  useEffect(() => {
    if (annualIncome && affordabilityRatio && debt) {
      const calculatedMaxHomePrice = annualIncome * affordabilityRatio - debt;
      setMaxHomePrice(calculatedMaxHomePrice);
    } else {
      setMaxHomePrice(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [annualIncome, affordabilityRatio, debt]);

  return (
    <Layout title="Home Affordability Calculator" path="home-affordability">
      <section className="section-padding" id="home-affordability-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Home Affordability Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Home Affordability Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Annual Income Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="annualIncome" className="form-label">
                        Annual Income (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Annual Income:</strong> Your total income earned in a year before tax deductions."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="annualIncome"
                        placeholder="Enter annual income"
                        value={annualIncome || ""}
                        onChange={handleAnnualIncomeChange}
                      />
                    </div>

                    {/* Affordability Ratio Input */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="affordabilityRatio"
                        className="form-label"
                      >
                        Affordability Ratio (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Affordability Ratio:</strong> The percentage of your income you can spend on housing costs."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="affordabilityRatio"
                        placeholder="Enter affordability ratio"
                        value={affordabilityRatio || ""}
                        onChange={handleAffordabilityRatioChange}
                      />
                    </div>

                    {/* Existing Debt Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="debt" className="form-label">
                        Existing Debt (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Existing Debt:</strong> The total debt you currently owe (e.g., loans, credit card debt)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="debt"
                        placeholder="Enter existing debt"
                        value={debt || ""}
                        onChange={handleDebtChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Maximum Home Price:{" "}
                        <span className="fw-bold ms-3">
                          {maxHomePrice === 0
                            ? "0.00"
                            : maxHomePrice.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Home Affordability Calculator
                </h3>

                {/* What is a Home Affordability Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Home Affordability Calculator?
                </h4>
                <p>
                  A **Home Affordability Calculator** helps you estimate the
                  maximum home price you can afford based on your annual income,
                  affordability ratio, and existing debt. It helps you assess
                  how much you can realistically spend on a home without
                  overextending your finances.
                </p>

                {/* Why Use a Home Affordability Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Home Affordability Calculator?
                </h4>
                <p>
                  This calculator is essential for homebuyers who want to know
                  how much home they can afford without straining their
                  finances. It also provides lenders with insight into a buyer’s
                  eligibility for a mortgage.
                </p>

                {/* Benefits of Using a Home Affordability Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Home Affordability Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps Buyers Determine Affordable Home Options:
                    </strong>{" "}
                    Provides a clear picture of what price range is affordable
                    based on income and existing debt.
                  </li>
                  <li>
                    <strong>
                      Provides Lenders Insight into Buyer Eligibility:
                    </strong>{" "}
                    Helps lenders assess whether a potential buyer is eligible
                    for a loan.
                  </li>
                  <li>
                    <strong>
                      Aids in Realistic Financial Planning for Homeownership:
                    </strong>{" "}
                    Assists buyers in creating a realistic home-buying plan
                    based on their financial situation.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Home Affordability Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Homebuyers:</strong> Helps individuals understand
                    how much home they can afford before committing to a
                    property.
                  </li>
                  <li>
                    <strong>Real Estate Agents:</strong> Assists agents in
                    guiding clients toward homes within their budget.
                  </li>
                  <li>
                    <strong>Mortgage Lenders:</strong> Provides lenders with an
                    idea of buyer eligibility for mortgage approval.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How is the home affordability ratio determined?
                </h4>
                <p>
                  The affordability ratio is typically calculated by lenders
                  based on industry standards, considering factors such as
                  monthly debt payments, annual income, and overall financial
                  health.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is it important to consider existing debt when calculating
                  affordability?
                </h4>
                <p>
                  Existing debt significantly impacts your ability to afford a
                  mortgage. Lenders and buyers alike need to account for
                  outstanding debt to ensure that the new mortgage won’t strain
                  finances beyond sustainable limits.
                </p>

                <h4 className="mt-4 mb-3">
                  What other factors should be considered when purchasing a
                  home?
                </h4>
                <p>
                  In addition to income, affordability ratio, and debt, buyers
                  should consider factors like property taxes, insurance, and
                  long-term maintenance costs when evaluating what they can
                  afford.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomeAffordability;
